<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <FormulateForm
            v-if="formReady"
            v-model="currentFormData"
            :name="formName"
            #default="{ isValid, isLoading }"
            @submit="handleSubmit"
        >
            <FormulateInput
                name="name"
                type="text"
                focus
                label="Name"
                placeholder="Name of this item"
                help="A descriptive name, visible to suppliers"
                :disabled="isLocked"
                :outer-class="['formulate-horizontal']"
            />
            <FormulateInput
                name="group"
                type="select"
                label="Group"
                help="The group this item belongs to, if any."
                :options="groupOptions"
                :disabled="isLocked"
                :outer-class="['formulate-horizontal']"
            />
            <FormulateInput
                name="description"
                type="textarea"
                label="Description"
                placeholder="Describe what this item is for..."
                rows="3"
                help="Optional - visible to suppliers"
                :disabled="isLocked"
                :outer-class="['formulate-horizontal']"
            />
            <!-- add read-only version_number?  -->
            <div class="mt-4 mb-3 d-flex justify-content-center">
                <!-- <span v-if="hasChanges" class="text-danger">changes</span> -->
                <b-button
                    variant="gray-secondary"
                    size="lg"
                    class="mr-3"
                    @click="triggerCancel"
                >
                    <span>Cancel</span>
                </b-button>
                <b-button
                    variant="primary"
                    size="lg"
                    :disabled="!isValid || isLoading || saving || isLocked || !formReady || !hasChanges"
                    @click="triggerSubmit"
                >
                    <b-spinner small v-if="saving" key="uploading" class="mr-2"></b-spinner>
                    <b-icon v-else key="uploadReady" icon="upload" class="mr-2"></b-icon>
                    <span>{{ saveButtonText }}</span>
                </b-button>
            </div>
        </FormulateForm>
        <div v-else key="stillLoading" class="min-height-300 w-100 d-flex align-items-center justify-content-center">
            <b-spinner label="Loading..."></b-spinner>
        </div>
    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'DocHubResourceForm',
    components: {},
    mixins: [
        errorHandling,
    ],
    props: {
        existingData: {  // if no ID, then we are doing a CREATE, otherwise provide one to do an update
            type: Object,
            required: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,  
            saving: false,
            formName: 'theForm',
            formReady: false,  // set true when you're done prepping initial data, if any
            initialFormData: {},  // if you are updating existing, must put here (goes to value prop, not v-model)
            sortOrder: null,  // we grab this out of initial data if it exists, then set it back on submitted data for API if it was not null.... because it is not a field on the form (could make it a hidden one?)
            loadingGroups: false,
            groupOptions: [],
            currentFormData: {},  // the v-model with form 
            lastSavedFormData: {},  // for checking if form has changes - DO NOT MUTATE (until you get a response back?)
            hasChanges: false,  // updated from watcher
        }
    },
    computed: {
        isLocked() {
            // do we want to lock form?  add logic later...
            return false;
        },
        isUpdate() {
            return !!this.existingData?.id;
        },
        saveButtonText() {
            return this.saving ? 'Saving' : (this.isUpdate ? 'Save' : 'Save & Next Step');
        },
    },
    watch: {
        currentFormData: {
            handler: function(newVal) {
                // check if form has changes
                const newValueStringified = JSON.stringify(newVal);
                const oldValueStringified = JSON.stringify(this.lastSavedFormData);
                this.hasChanges = newValueStringified !== oldValueStringified;
                //console.log('WATCH of currentFormData, hasChanges?', this.hasChanges, 'new vs old:', newValueStringified, oldValueStringified);
            },
            deep: true,
        },
    },
    async mounted() {
        // do something depending on being a create/update?
        const existingData = this.existingData || {};
        this.setInitialFormData(existingData);
        // if (this.isUpdate) {
        //     this.setInitialFormData(existingData);
        // } else {
        //     this.setInitialFormData(existingData);  // still set existingData for create, we likely pass in group (ID)
        // }
        await this.getGroupOptions();
        console.log('from after getGroupOptions in mounted hook - this.groupOptions:', this.groupOptions, 'this.currentFormData:', this.currentFormData, 'this.initialFormData:', this.initialFormData);
        this.formReady = true;
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        setInitialFormData(existingData) {
            // pass in versionData from your API, this has to for
            // destructure and pull out only the fields we want so our change detection is cleaner
            this.initialFormData = this.extractFormData(existingData); // cloneDeep(existingData);
            if (existingData.sort_order || existingData.sort_order === 0) {
                this.sortOrder = existingData.sort_order;
            }
            this.setLastSavedFormData(existingData);
            this.currentFormData = this.extractFormData(existingData); // cloneDeep(existingData);
        },
        extractFormData(data) {
            // pull out only the fields we want so our change detection is cleaner -- and make sure this always returns a fresh object (with cloneDeep if you have nested stuff)
            return { 
                name: data.name || '',
                group: data.group || null,
                description: data.description || '',
            };
        },
        setLastSavedFormData(data) {
            this.lastSavedFormData = this.extractFormData(data);
        },
        triggerSubmit() {
            this.$formulate.submit(this.formName);
        },
        triggerCancel() {
            this.$emit('cancel');  // also need to reset things?  or let it get destroyed?
        },
        formatDataForSave(formData) {
            // do anything with the data before sending to API
            console.log('data we need to format for save:', formData);
            const dataForApi = cloneDeep(formData);  // copy this first, then we'll add files back in after 
            // add back the sort_order passed in initial data if we had it
            if (this.sortOrder || this.sortOrder === 0) {
                dataForApi.sort_order = this.sortOrder;
            }
            return dataForApi;
        },
        async getGroupOptions() {
            // get options for a select input
            this.loadingGroups = true;
            let resp;
            try {
                resp = await suppliosApi.dochubResource.get_group_options();
            } catch (err) {
                this.apiErrorHandler(err);
            }
            if (resp?.data) {
                const optionsFromApi = resp.data.map(g => {
                    return { value: g.id, label: g.name };  // need to remap to value/label for formulate
                });
                // const emptyOption = { value: null, label: '(No Group)' };
                // this.groupOptions = [emptyOption, ...optionsFromApi];
                // NO MORE EMPTY GROUP OPTION 
                this.groupOptions = optionsFromApi;
            }
            console.log('from after getGroupOptions in method');
            this.loadingGroups = false;
        },
        setSavingStatus(isSaving) {
            this.saving = isSaving;
            this.$emit('loading-status', isSaving);
        },
        async handleSubmit(formData) {  // called on the FormulateForm @submit event
            // this should be async, return a Promise, so Formulate shows isLoading while loading
            console.log('*** From handleSubmit, here is formData we got:');
            console.log(formData);
            let dataToSubmit = this.formatDataForSave(formData);
            this.setSavingStatus(true);
            let returnData;
            try {
                if (this.isUpdate) {
                    const resourceId = this.existingData.id;
                    const resp = await suppliosApi.dochubResource.update(resourceId, dataToSubmit);  
                    returnData = resp.data;
                    console.log('got response from updating resource', returnData);
                } else {  // if NOT an update, create new version
                    const resp = await suppliosApi.dochubResource.create(dataToSubmit);
                    returnData = resp.data;
                    console.log('got response from creating new resource', returnData);
                }
            } catch (err) {
                this.apiErrorHandler(err);
            }
            if (returnData) {
                this.setLastSavedFormData(returnData);
                // must make sure to complete all the things we need to do locally after saving before emitting to parent so it doens't kill us before we're done
                if (this.isUpdate) {
                    this.$emit('updated', returnData);
                } else {
                    this.$emit('created', returnData);
                }
                this.$emit('saved', returnData);  // common to both update/create
            }
            this.setSavingStatus(false);
            return returnData;  // make sure not to return until after you set saving status back to false
        },
    }
}
</script>

<style>

</style>